import { useCallback, useEffect, useRef } from "react";
import { markDirty, registerFlusher } from "../api/dirtyTracker.js";

export interface UseAutosaveOptions {
  /**
   * Stable identity for this autosave session — e.g. a node id or a
   * `attempt-answer:${questionId}` string. Dirty-tracking and flush
   * registration are scoped to this key. Autosave sessions are expected to
   * remount (not re-key) when the underlying record changes — mirror
   * WritingPanel's existing `key={node.id}` remount pattern on the owning
   * component.
   */
  key: string;
  /** Set false to pause all timers/dirty-tracking (e.g. no record selected yet). Default true. */
  enabled?: boolean;
  /** Debounce after the value changes, before attempting a save. Default 1400ms. */
  debounceMs?: number;
  /** Periodic retry tick. Default 4000ms. */
  intervalMs?: number;
  /** Upper bound for the backed-off retry period after consecutive failures. Default 30000ms. */
  maxIntervalMs?: number;
}

export interface UseAutosaveHandle {
  /** Force-save the current value right now (e.g. on blur). Resolves once
   * the value is confirmed saved (or was already up to date); resolves
   * `false` on failure — most callers (onBlur) fire this without awaiting,
   * but flush-style callers (submit, pre-redirect, tab-hidden) do await it. */
  saveNow: () => Promise<boolean>;
}

const DEFAULT_DEBOUNCE_MS = 1400;
const DEFAULT_INTERVAL_MS = 4000;
const DEFAULT_MAX_INTERVAL_MS = 30000;

/**
 * Shared autosave engine: debounce + periodic-retry + save-on-unmount +
 * a queue that serializes overlapping saves for one key, wired into the
 * WP-08 dirty registry so a pending draft (a) blocks `beforeunload`,
 * (b) participates in `flushAll()` (tab-hidden flush, pre-redirect flush,
 * test-submit flush).
 *
 * On failure: the draft is kept, `key` stays marked dirty (the caller's
 * `saveFn` already goes through `api/commands.ts` `run()`, which pushes the
 * error toast and flips the save-status pill to "failed" — this hook does
 * not duplicate that), and the interval keeps retrying with a bounded
 * backoff (doubling per consecutive failure, capped at `maxIntervalMs`) so
 * a dead API isn't hammered every tick.
 *
 * Overlapping saves for the same key are serialized through a promise-chain
 * mutex (`queueRef`) rather than a fire-and-forget flag: every call to
 * `saveNow()` is appended after whatever is currently running and re-reads
 * the live value once its turn comes, so (a) writes for one key can never
 * land out of order and (b) a flush-style caller gets back the TRUE outcome
 * for the value as of its call, not just "a save was in flight".
 *
 * Ported from WritingPanel's original inline implementation
 * (debounce ~1.4s + interval ~4s + save-on-blur + save-on-unmount + a
 * saving/queued guard) — see WritingPanel.tsx and TestAttemptScreen.tsx for
 * the two call sites. `T` must be safely comparable with `!==` (both
 * current uses are plain strings).
 */
export function useAutosave<T>(
  value: T,
  saveFn: (value: T) => Promise<unknown>,
  options: UseAutosaveOptions
): UseAutosaveHandle {
  const {
    key,
    enabled = true,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxIntervalMs = DEFAULT_MAX_INTERVAL_MS,
  } = options;

  const valueRef = useRef(value);
  // Seeded from the value at first render — mirrors WritingPanel's
  // `lastSavedRef = useRef(node?.writing.markdown ?? "")`: the baseline is
  // "whatever the server already has", not empty.
  const lastSavedRef = useRef(value);
  const failureCountRef = useRef(0);
  const dirtyReleaseRef = useRef<(() => void) | null>(null);
  const saveFnRef = useRef(saveFn);
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const enabledRef = useRef(enabled);
  const saveNowRef = useRef<() => Promise<boolean>>(async () => true);

  // Refs are only ever read from event handlers/timers/effects below, never
  // during render — the assignments themselves must happen in an effect too
  // (react-hooks/refs forbids mutating a ref's `.current` during render).
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    saveFnRef.current = saveFn;
  }, [saveFn]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const releaseDirty = useCallback(() => {
    dirtyReleaseRef.current?.();
    dirtyReleaseRef.current = null;
  }, []);

  // Read by attemptSave, whose async body can resume after this component is
  // gone (see the guard there).
  const unmountedRef = useRef(false);

  // Mark `key` dirty the instant the value diverges from the last confirmed
  // save — NOT only once a save attempt actually starts (that would leave a
  // ~1.4s window right after typing where hasDirtyWork() is falsely false,
  // and closing the tab in that window wouldn't warn). This effect only ever
  // marks; attemptSave's success path below is what releases it.
  useEffect(() => {
    if (!enabled || value === lastSavedRef.current) return;
    if (!dirtyReleaseRef.current) dirtyReleaseRef.current = markDirty(key);
  }, [value, enabled, key]);

  const attemptSave = useCallback((): Promise<boolean> => {
    const next = queueRef.current.then(async () => {
      if (!enabledRef.current) return true;
      const attempt = valueRef.current;
      if (attempt === lastSavedRef.current) return true;

      // Never (re-)mark dirty once unmounted. The unmount cleanups run in
      // declaration order: the interval effect's cleanup fires a best-effort
      // save, and the final cleanup below then releases the dirty key — but
      // this body only resumes as a microtask *after* both have run. Without
      // this guard it would re-mark the key, and a failing save has nothing
      // left alive to release it, stranding the key in the shared tracker for
      // the rest of the session: a permanent phantom "unsaved changes" prompt
      // with no draft left anywhere to save.
      if (!dirtyReleaseRef.current && !unmountedRef.current) dirtyReleaseRef.current = markDirty(key);
      try {
        await saveFnRef.current(attempt);
        // Only advance the "last saved" baseline on CONFIRMED success —
        // never optimistically, and never reset it to a synthetic value on
        // failure. (WritingPanel's old failure path reset this to "" and
        // retried invisibly; a genuinely empty draft would then look
        // already-saved and mask a real failure. Leaving the baseline
        // untouched on failure fixes that.)
        lastSavedRef.current = attempt;
        failureCountRef.current = 0;
        if (valueRef.current === lastSavedRef.current) releaseDirty();
        return true;
      } catch {
        // commands.ts's run() already toasted the failure and flipped the
        // save-status pill — we just keep the draft dirty and let the next
        // interval tick retry.
        failureCountRef.current = Math.min(failureCountRef.current + 1, 10);
        return false;
      }
    });
    queueRef.current = next;
    return next;
  }, [key, releaseDirty]);

  useEffect(() => {
    saveNowRef.current = attemptSave;
  }, [attemptSave]);

  const saveNow = useCallback(() => saveNowRef.current(), []);

  // Debounce: save shortly after the value settles.
  useEffect(() => {
    if (!enabled || value === lastSavedRef.current) return;
    const timeout = window.setTimeout(() => void saveNow(), debounceMs);
    return () => window.clearTimeout(timeout);
  }, [value, enabled, debounceMs, saveNow]);

  // Periodic retry tick. A literal `setInterval` (not a rescheduled
  // `setTimeout`) so the *cadence* matches WritingPanel's original timing
  // exactly in the success path; bounded backoff is layered on by skipping
  // ticks while `failureCountRef` is nonzero, rather than by changing the
  // interval's own period.
  useEffect(() => {
    if (!enabled) return;
    let tickCount = 0;
    const interval = window.setInterval(() => {
      tickCount += 1;
      const failures = failureCountRef.current;
      const skipTicks = failures === 0 ? 1 : Math.min(2 ** failures, Math.ceil(maxIntervalMs / intervalMs));
      if (tickCount % skipTicks !== 0) return;
      void saveNowRef.current();
    }, intervalMs);
    return () => {
      window.clearInterval(interval);
      // Best-effort save-on-unmount, same as WritingPanel today.
      void saveNowRef.current();
    };
  }, [enabled, intervalMs, maxIntervalMs]);

  // Participate in on-demand flushes (tab hidden, pre-redirect, test submit).
  useEffect(() => {
    if (!enabled) return;
    return registerFlusher(key, () => saveNowRef.current());
  }, [enabled, key]);

  // Belt-and-suspenders: never leave `key` stuck dirty past unmount, even if
  // a save never resolves (e.g. the component unmounts mid-request).
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      releaseDirty();
    };
  }, [releaseDirty]);

  return { saveNow };
}
